import WithLessonUtilsHOC, { WithLessonUtilsProp } from '@hocs/WithLessonUtils'
import WithUserTimeHOC, { WithUserTimeProps } from '@hocs/WithUserTime'
import { SessionInfoList } from '@interfaces/LessonGroups'
import { ScheduleSession } from '@interfaces/ScheduleSession'
import { GET_MATERIAL, GET_SCHEDULES_ID } from '@queries/index'
import get from 'lodash/get'
import React from 'react'
import { withApollo, WithApolloClient } from 'react-apollo'
import { LessonHistory } from '@interfaces/LessonHistory'
import { find } from 'lodash'
import { getLessonHistoryVariables } from '@utils/helpers/lesson-history'
import moment from 'moment'
import { UserProfileProps } from '@interfaces/UserProfile'

interface QueryPageResults {
  loading: boolean
  data: any[] | undefined
  student: SessionInfoList | undefined
  setStudent: (student: SessionInfoList) => void
  setData: (data?: any[]) => void
}

interface QueryLessonHistoryProps {
  fromDate: string
  endDate: string
  learningPaths: any
  students: SessionInfoList[]
  fetchFlex: boolean
  fetchLCS: boolean
  fetchBoD1: boolean
  currentInstructor: boolean
  language: string
  ProgramIds: string[]
  userProfile: UserProfileProps
  children: (results: QueryPageResults) => {}
}

type Props = WithApolloClient<QueryLessonHistoryProps> & WithUserTimeProps & WithLessonUtilsProp

class QueryLessonHistory extends React.PureComponent<Props> {
  _isMounted = false
  abortCtrl = new AbortController()
  materialAbortCtrl = new AbortController()

  state = {
    loading: false,
    data: undefined as any[] | undefined,
    originalData: undefined as any[] | undefined,
    student: undefined as SessionInfoList | undefined,
  }

  async componentDidMount() {
    const { students } = this.props
    this._isMounted = true
    this.fetch(students[0])
  }

  componentWillUnmount() {
    this._isMounted = false
    this.abortCtrl.abort()
    this.materialAbortCtrl.abort()
  }

  fetch = async (student: SessionInfoList) => {
    const { fetchFlex, fetchLCS, fetchBoD1, profile, currentInstructor, language } = this.props
    this.setState({ loading: true })
    try {
      const schedules = await this.fetchLessonHistory(student, fetchBoD1)

      let lessons: LessonHistory[] = []
      if (fetchFlex) {
        lessons = this.props.getAllFlexLessonHistory(schedules, this.props.toUserDate, language)
      } else if (fetchBoD1) {
        lessons = this.props.getAllBoD1History(schedules, this.props.toUserDate, language)
      } else if (fetchLCS) {
        lessons = this.props.getAllLiveCoachingHistory(schedules, this.props.toUserDate, language)
      } else {
        lessons = this.props.getAllLessonHistory(schedules, this.props.toUserDate, language)
      }

      lessons = this.getMaterialInfo(lessons)

      // check for missing materials
      // and make request to LMS
      lessons = await this.getMaterial(lessons)

      if (currentInstructor) {
        lessons = this.props.getCurrentInstLessonHistory(lessons, profile)
      }

      if (this._isMounted) {
        this.setState({
          data: lessons,
          originalData: lessons,
          loading: false,
          student,
        })
      }
    } catch (error) {
      if (this._isMounted) {
        this.setState({ loading: false })
      }
    }
  }

  setStudent = (student: SessionInfoList) => {
    this.setState({ student })
    this.fetch(student)
  }

  setData = (data: any[] | undefined) => {
    if (data) {
      this.setState({ data })
    } else {
      this.setState({ data: this.state.originalData })
    }
  }

  getMaterialInfo = (lessons: any[]): any[] => {
    const { learningPaths } = this.props
    return lessons.map((lesson) => {
      const materials = lesson.materials.map((material) => {
        if (material.Material !== 'Own Material') {
          const lp = learningPaths?.find((val) => val.lpid === material.Material)
          if (!lp) {
            return material
          }

          let MaterialName = material.MaterialName
          let ChapterName = material.ChapterName

          if (!MaterialName) {
            MaterialName = lp.shortname || get(lp, 'details.fullname.0.fullname')
          }

          const unit = lp.details.units.find((u) => u.unitid === parseInt(material.Chapter, 10))

          if (!ChapterName) {
            ChapterName = get(unit, 'fullname')
          }

          return {
            ...material,
            MaterialName,
            ChapterName,
          }
        }

        return material
      })

      return {
        ...lesson,
        materials,
      }
    })
  }

  getMaterial = async (lessons): Promise<any> => {
    const { client, learningPaths } = this.props
    const promisedLessons = lessons.map(async (lesson) => {
      const materials = lesson.materials.map(async (material) => {
        if (material.Material !== 'Own Material' && !material.MaterialName) {
          const lp = learningPaths.find((val) => val.lpid === material.Material)
          if (!lp && material.Material) {
            const materialInfo = await client.query({
              query: GET_MATERIAL,
              variables: { Id: material.Material },
              fetchPolicy: 'network-only',
              context: {
                fetchOptions: {
                  signal: this.materialAbortCtrl.signal,
                },
              },
            })

            const units = get(materialInfo.data, 'getLearningPathById.units')
            const unit = find(units, { unitid: parseInt(material.Chapter, 10) })

            return {
              ...material,
              MaterialName: get(
                materialInfo.data,
                'getLearningPathById.shortname',
                get(materialInfo.data, 'getLearningPathById.fullname.0.fullname', material.Material)
              ),
              ChapterName: get(unit, 'fullname'),
            }
          }
        }

        return material
      })

      return {
        ...lesson,
        materials: await Promise.all(materials),
      }
    })

    return Promise.all(promisedLessons)
  }

  fetchLessonHistory = async (
    student: SessionInfoList,
    WithStudentNote: boolean = false
  ): Promise<ScheduleSession[]> => {
    const { client, fromDate, endDate, ProgramIds = [] } = this.props

    /**
     * Business wants to see all lessons including future lessons
     * however FE is only capable of getting lesson history by date range
     * so we need to add 2 months to the end date to get all possible future lessons
     */
    const EndDate = moment.utc(endDate).add(2, 'months').format()

    const variables = getLessonHistoryVariables({
      Id: student.CustomerId,
      FilterType: 'FILTER_LESSON_HISTORY',
      Limit: 100,
      FromDate: fromDate,
      EndDate,
      ProgramIds,
      WithStudentNote,
      UserId: this.props.userProfile.SFId,
    })

    const res = await client.query({
      query: GET_SCHEDULES_ID,
      variables,
      fetchPolicy: 'network-only',
      context: {
        fetchOptions: {
          signal: this.abortCtrl.signal,
        },
      },
    })

    const historyLessons = get(res.data, 'getSchedulesId', [])

    let allLessons = historyLessons

    return allLessons
  }

  render() {
    const { children } = this.props
    const { loading, data, student } = this.state

    return <>{children({ loading, data, setStudent: this.setStudent, setData: this.setData, student })}</>
  }
}

export default WithLessonUtilsHOC(withApollo<WithLessonUtilsProp>(WithUserTimeHOC(QueryLessonHistory)))
